import { useEffect } from 'react';
import type { RefObject } from 'react';
import { useLocation } from 'react-router-dom';

const useRevealAnimation = <T extends HTMLElement>(containerRef: RefObject<T | null>) => {
    const { pathname } = useLocation();

    useEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return;
        }

        const observedElements = new WeakSet<HTMLElement>();

        const observeRevealElements = (observer: IntersectionObserver) => {
            const revealElements = Array.from(container.querySelectorAll<HTMLElement>('.reveal'));
            revealElements.forEach((element) => {
                if (observedElements.has(element)) {
                    return;
                }

                element.classList.remove('active');
                observer.observe(element);
                observedElements.add(element);
            });
        };

        let observer: IntersectionObserver | null = null;
        let mutationObserver: MutationObserver | null = null;
        let frameId = window.requestAnimationFrame(() => {
            observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            entry.target.classList.add('active');
                            observer?.unobserve(entry.target);
                        }
                    });
                },
                { threshold: 0.15 }
            );

            observeRevealElements(observer);

            mutationObserver = new MutationObserver(() => {
                if (!observer) {
                    return;
                }

                observeRevealElements(observer);
            });

            mutationObserver.observe(container, {
                childList: true,
                subtree: true,
            });
        });

        return () => {
            window.cancelAnimationFrame(frameId);
            mutationObserver?.disconnect();
            observer?.disconnect();
        };
    }, [pathname, containerRef]);
};

export default useRevealAnimation;
